import { useTheme } from "../hooks/useTheme";
import type { ThemePreference } from "../lib/theme";

const CYCLE: ThemePreference[] = ["light", "dark", "system"];

const LABELS: Record<ThemePreference, string> = {
	light: "Lights Up",
	dark: "Lights Down",
	system: "Match system",
};

function nextPreference(current: ThemePreference): ThemePreference {
	const i = CYCLE.indexOf(current);
	return CYCLE[(i + 1) % CYCLE.length]!;
}

function SunIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
			<circle cx="9" cy="9" r="3.25" stroke="currentColor" strokeWidth="1.5" />
			<path
				d="M9 2.25v1.5M9 14.25v1.5M2.25 9h1.5M14.25 9h1.5M4.05 4.05l1.06 1.06M12.89 12.89l1.06 1.06M4.05 13.95l1.06-1.06M12.89 5.11l1.06-1.06"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function MoonIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
			<path
				d="M14.4 10.65A5.75 5.75 0 0 1 7.35 3.6 5.9 5.9 0 1 0 14.4 10.65Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** 8-point starburst (union outline of two squares) with capital A — system / auto. */
function SystemIcon() {
	return (
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
			<path
				d="M9 2.9 13.25 4.75 15.1 9 13.25 13.25 9 15.1 4.75 13.25 2.9 9 4.75 4.75Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinejoin="round"
			/>
			<text
				x="9"
				y="9.65"
				textAnchor="middle"
				dominantBaseline="middle"
				fill="currentColor"
				fontSize="6"
				fontWeight="600"
				fontFamily="ui-sans-serif, system-ui, sans-serif"
			>
				A
			</text>
		</svg>
	);
}

function ThemeIcon({ value }: { value: ThemePreference }) {
	if (value === "light") return <SunIcon />;
	if (value === "dark") return <MoonIcon />;
	return <SystemIcon />;
}

/**
 * Single-icon theme toggle — cycles light → dark → system.
 * Icon + helptext reflect the current preference; soft resting chrome.
 */
export function ThemeControl() {
	const { preference, setPreference } = useTheme();
	const label = LABELS[preference];

	return (
		<div className="group relative">
			<button
				type="button"
				aria-label={`Theme: ${label}. Click to switch`}
				onClick={() => setPreference(nextPreference(preference))}
				className={[
					"relative inline-flex size-9 items-center justify-center rounded-[10px] text-wi-text transition-colors duration-150 ease-in-out",
					"bg-wi-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/45",
					"hover:bg-wi-gold-soft/60",
				].join(" ")}
			>
				<ThemeIcon value={preference} />
			</button>
			<span
				role="tooltip"
				className={[
					"pointer-events-none absolute top-full left-1/2 z-20 mt-1.5 -translate-x-1/2",
					"whitespace-nowrap rounded-full border border-wi-text/10 bg-wi-surface px-2.5 py-1",
					"font-medium text-[11px] text-wi-text shadow-none",
					"opacity-0 transition-opacity duration-100 ease-in-out",
					"group-hover:opacity-100 group-focus-within:opacity-100",
					"motion-reduce:transition-none",
				].join(" ")}
			>
				{label}
			</span>
		</div>
	);
}
